import dayjs, { Dayjs } from 'dayjs';

/**
 * 解析Excel日期/时间值
 * 支持的格式：
 * 1. Excel数字格式（1-5位数字，表示从1900-01-01开始的天数）
 * 2. Excel时间小数格式（0.508472222222222，表示一天中的比例）
 * 3. Excel日期时间混合格式（42716.5084722222，整数部分是日期，小数部分是时间）
 * 4. 字符串日期格式：2026-01-01, 2026-1-1, 2026/01/01, 2026/1/1, 等
 * 5. 字符串时间格式：14:30:00, 14:30, 2026-01-01 14:30:00, 等
 * 6. dayjs对象
 * 
 * @param value - 需要解析的值
 * @param type - 字段类型：'datepicker' 或 'timepicker'
 * @param format - 输出格式，默认日期'YYYY-MM-DD'，时间'HH:mm:ss'
 * @returns { value: string, isValid: boolean, error?: string }
 */
export function parseExcelDate(
  value: any,
  type: 'datepicker' | 'timepicker' = 'datepicker',
  format?: string,
): { value: string; isValid: boolean; error?: string } {
  if (value === undefined || value === null || value === '') {
    return { value: '', isValid: true };
  }

  const defaultFormat = type === 'datepicker' ? 'YYYY-MM-DD' : 'HH:mm:ss';
  const outputFormat = format || defaultFormat;

  try {
    let parsedDate: Dayjs | null = null;

    // 情况1：纯数字格式（Excel序列号或时间小数）- 仅处理真正的数字类型
    // 字符串格式的纯数字会放在字符串解析流程中
    if (typeof value === 'number') {
      // 检查是否为小数（时间格式或日期+时间）
      if (value > 0 && value < 1) {
        // Excel时间小数格式：0.508472222222222 表示一天中的时间比例
        const totalSeconds = Math.round(value * 24 * 60 * 60);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        // 创建一个基准日期，然后设置时间
        parsedDate = dayjs()
          .hour(hours)
          .minute(minutes)
          .second(seconds);
      } else if (value >= 1) {
        // 检查是否包含小数部分（日期+时间混合）
        const decimalPart = value - Math.floor(value);
        
        if (decimalPart > 0) {
          // 日期+时间混合格式：42716.5084722222
          const excelEpoch = dayjs('1900-01-01');
          const datePart = Math.floor(value);
          
          // 处理Excel的1900年闰年bug
          let dateResult: Dayjs;
          if (datePart <= 60) {
            dateResult = excelEpoch.add(datePart - 1, 'day');
          } else {
            dateResult = excelEpoch.add(datePart - 2, 'day');
          }
          
          // 处理时间部分
          const totalSeconds = Math.round(decimalPart * 24 * 60 * 60);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          
          parsedDate = dateResult
            .hour(hours)
            .minute(minutes)
            .second(seconds);
        } else {
          // 纯日期序列号：1表示1900-01-01
          const excelEpoch = dayjs('1900-01-01');
          const daysSinceEpoch = Math.floor(value);
          
          // 有效的Excel日期范围检查（1-60000）
          if (daysSinceEpoch >= 1 && daysSinceEpoch <= 60000) {
            // 处理Excel的1900年闰年bug
            if (daysSinceEpoch <= 60) {
              // 1-60号之间直接处理（包括虚拟的2月29日）
              parsedDate = excelEpoch.add(daysSinceEpoch - 1, 'day');
            } else {
              // 61及以后减1来跳过虚拟的2月29日
              parsedDate = excelEpoch.add(daysSinceEpoch - 2, 'day');
            }
          } else {
            // 如果数字不在有效范围内，作为普通字符串再尝试解析
            parsedDate = null;
          }
        }
      }
    }
    
    // 情况2：字符串格式（包括字符串格式的纯数字和小数）
    if (!parsedDate && typeof value === 'string') {
      const trimmedValue = value.trim();

      // 检查是否为小数字符串（时间或日期+时间格式）
      // 例如：\"0.508472222222222\" 表示时间，\"42716.5084722222\" 表示日期+时间
      if (/^\d+\.\d+$/.test(trimmedValue)) {
        const decimalValue = parseFloat(trimmedValue);
        
        if (decimalValue >= 0 && decimalValue < 1) {
          // 纯时间小数格式：0-0.99999 对应 00:00:00-23:59:59
          const totalSeconds = Math.round(decimalValue * 24 * 60 * 60);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          
          // 创建一个基准日期，然后设置时间
          parsedDate = dayjs()
            .hour(hours)
            .minute(minutes)
            .second(seconds);
        } else if (decimalValue >= 1) {
          // 日期+时间混合格式：42716.5084722222 = 42716天 + 0.5084722222天的时间
          const excelEpoch = dayjs('1900-01-01');
          const datePart = Math.floor(decimalValue);
          const timePart = decimalValue - datePart;
          
          // 处理Excel的1900年闰年bug
          let dateResult: Dayjs;
          if (datePart <= 60) {
            dateResult = excelEpoch.add(datePart - 1, 'day');
          } else {
            dateResult = excelEpoch.add(datePart - 2, 'day');
          }
          
          // 处理时间部分
          const totalSeconds = Math.round(timePart * 24 * 60 * 60);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          
          parsedDate = dateResult
            .hour(hours)
            .minute(minutes)
            .second(seconds);
        }
      }

      // 检查是否为纯数字字符串，如果是则作为Excel序列号处理
      if (!parsedDate && /^\d+$/.test(trimmedValue)) {
        const excelEpoch = dayjs('1900-01-01');
        const daysSinceEpoch = parseInt(trimmedValue, 10);
        
        // 有效的Excel日期范围检查（1-60000）
        if (daysSinceEpoch >= 1 && daysSinceEpoch <= 60000) {
          // 处理Excel的1900年闰年bug
          if (daysSinceEpoch <= 60) {
            parsedDate = excelEpoch.add(daysSinceEpoch - 1, 'day');
          } else {
            parsedDate = excelEpoch.add(daysSinceEpoch - 2, 'day');
          }
        }
      }

      // 如果不是纯数字或解析失败，尝试作为日期字符串解析
      if (!parsedDate) {
        // 尝试多种日期格式
        const formats = [
          'YYYY-MM-DD HH:mm:ss', // 2026-01-01 14:30:00
          'YYYY-MM-DD HH:mm',    // 2026-01-01 14:30
          'YYYY-MM-DD',          // 2026-01-01
          'YYYY/MM/DD HH:mm:ss', // 2026/01/01 14:30:00
          'YYYY/MM/DD HH:mm',    // 2026/01/01 14:30
          'YYYY/MM/DD',          // 2026/01/01
          'HH:mm:ss',            // 14:30:00
          'HH:mm',               // 14:30
        ];

        // 特殊处理格式如 "2026-1-1" 或 "2026/1/1"
        let normalizedValue = trimmedValue;
        
        // 检测是否为 YYYY-M-D 或 YYYY/M/D 格式并转换为 YYYY-MM-DD
        const singleDigitDateMatch = trimmedValue.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(.*)$/);
        if (singleDigitDateMatch) {
          const [, year, month, day, rest] = singleDigitDateMatch;
          const paddedMonth = month.padStart(2, '0');
          const paddedDay = day.padStart(2, '0');
          normalizedValue = `${year}-${paddedMonth}-${paddedDay}${rest}`;
        }

        // 尝试解析
        for (const fmt of formats) {
          const parsed = dayjs(normalizedValue, fmt, true);
          if (parsed.isValid()) {
            parsedDate = parsed;
            break;
          }
        }

        // 如果仍未解析，尝试dayjs的智能解析（作为最后手段）
        if (!parsedDate) {
          const fallbackParsed = dayjs(trimmedValue);
          if (fallbackParsed.isValid()) {
            parsedDate = fallbackParsed;
          }
        }
      }
    }
    
    // 情况3：dayjs对象
    if (!parsedDate && dayjs.isDayjs(value)) {
      parsedDate = value;
    }

    // 验证解析结果
    if (!parsedDate || !parsedDate.isValid()) {
      return {
        value: String(value),
        isValid: false,
        error: `值 "${value}" 无法解析为${type === 'datepicker' ? '日期' : '时间'}`,
      };
    }

    // 根据类型格式化输出
    let formattedValue: string;
    if (type === 'datepicker') {
      formattedValue = parsedDate.format(outputFormat);
    } else {
      // timepicker：仅提取时间部分
      formattedValue = parsedDate.format(outputFormat);
    }

    return {
      value: formattedValue,
      isValid: true,
    };
  } catch (err) {
    return {
      value: String(value),
      isValid: false,
      error: `值 "${value}" 解析失败`,
    };
  }
}

/**
 * 批量解析日期数组
 */
export function parseExcelDateArray(
  values: any[],
  type: 'datepicker' | 'timepicker' = 'datepicker',
  format?: string,
) {
  return values.map(v => parseExcelDate(v, type, format));
}
